import React from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Field, Form, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { requiredValidator } from './validators/FormValidator';
import { Button } from '@progress/kendo-react-buttons';
import { FieldMapper } from './FieldMapper';
import { FormUpload } from './fields/FormUpload';
import { useFieldInteractions, type FieldInteractionCallbacks } from './fieldInteractions/useFieldInteractions';
import { useDocumentFormInit } from './hooks/useDocumentFormInit';
import { useDocumentSubmission } from './hooks/useDocumentSubmission';
import { useFileUploadConfig } from './hooks/useFileUploadConfig';
import { useFieldGroups } from './hooks/useFieldGroups';
import { FormFileMetadataTable } from './fields/metadataGrid/FormFileMetadataTable';
import { FormContentTypeField } from './fields/FormContentTypeField';
import { FormFieldsSection } from './fields/FormFieldsSection';
import {
  shouldShowFileMetadataTable,
  shouldShowFileUploadBottom,
  shouldShowFileUploadTop
} from './DocumentFormHelpers';
import { FormRetryCheckbox } from './fields/FormRetryCheckbox';
import { FormErrorList } from './fields/FormErrorList';
import {
  useDocumentFormContext,
  useGetCtsWithCreatePermission,
  useGlobalizationContext,
  useTranslation,
  useGetAllFields,
  useGetCurrentUser,
  useGetContentType,
  useGetMultiFileUploadLimit
} from '@cs-cms/state-management';
import { NEW_DOCUMENT, ContentTypeFieldResponse, FILE_FIELD, CpsDocumentMetadata } from '@cs-cms/types';
import CpsLoader from '../common/CpsLoader';

const FORM_ID = 'GEAR_DOCUMENT_FORM';

export interface DocumentFormFieldRenderArgs {
  ctField: ContentTypeFieldResponse;
  contentTypeFields: ContentTypeFieldResponse[];
  fieldTranslation?: string;
  choices: string[];
  isLoading: boolean;
  isEnabled: boolean;
  formRenderProps: FormRenderProps;
  runLookupForField: (
    lookupFieldName: string,
    lookupValue: unknown,
    lookupFormRenderProps: FormRenderProps
  ) => Promise<{ valuesForOtherFields?: Record<string, unknown> } | null>;
  onChangeDefault: (value: any) => Promise<void>;
  onFilterChangeDefault: (value: any) => Promise<void>;
  onSearchDefault: (value: any) => Promise<void>;
  onRefreshDefault: (value: any) => Promise<void>;
}

export interface DocumentFormContentShellProps {
  indexId: number;
  renderField: (args: DocumentFormFieldRenderArgs) => React.ReactNode;
  renderCustomContent?: () => React.ReactNode;
  transformMetadataForSubmit?: (metadata: CpsDocumentMetadata) => CpsDocumentMetadata;
  fieldInteractionCallbacks?: FieldInteractionCallbacks;
}

interface DocumentFormContentProps {
  indexId: number;
}

export const DocumentFormContentShell: React.FC<DocumentFormContentShellProps> = ({
  indexId,
  renderField,
  renderCustomContent,
  transformMetadataForSubmit,
  fieldInteractionCallbacks
}) => {
  const { editedDocument, closeDocumentForm, initialFormData, isRetry, fileUploadRequired } = useDocumentFormContext();
  const { data: ctsWithCreatePermissions } = useGetCtsWithCreatePermission(indexId);
  const [attemptedSubmit, setAttemptedSubmit] = React.useState(false);
  const [retry, setRetry] = React.useState(false);
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { data: allFields } = useGetAllFields(selectedLocale, indexId);
  const { data: user } = useGetCurrentUser();

  const initCtName =
    editedDocument === NEW_DOCUMENT ? initialFormData?.contentType || undefined : editedDocument?.Cps_ContentType;
  const [selectedCtName, setSelectedCtName] = React.useState<string | undefined>(initCtName);
  const { data: contentType } = useGetContentType(selectedCtName, selectedLocale);
  const { data: multiFileUploadLimit } = useGetMultiFileUploadLimit(indexId);

  const dialogTitle = translate(
    editedDocument === NEW_DOCUMENT
      ? 'custom.toolbar.actions.form.addNewTitle'
      : 'custom.toolbar.actions.form.editTitle'
  );
  const allCtFields = contentType?.fields || [];
  const ctFields = (contentType?.fields || []).filter(field => !field.technical);

  const { allowsMultiFileUpload, perFileFields, maxFiles, needsToRenderFilesInTable } = useFileUploadConfig({
    contentType,
    ctFields,
    multiFileUploadLimit
  });

  const isNewDocument = editedDocument === NEW_DOCUMENT;

  const {
    initialFormValues,
    documentETag,
    formKey,
    isLoading: isInitLoading,
    existingDocumentData: editedDocData
  } = useDocumentFormInit({
    indexId,
    isNewDocument,
    isRetry,
    fileUploadRequired,
    existingDocumentId: editedDocument !== NEW_DOCUMENT ? editedDocument?.Cps_DocId : undefined,
    existingDocumentCtName: editedDocument !== NEW_DOCUMENT ? editedDocument?.Cps_ContentType : undefined,
    prefillData: initialFormData,
    contentTypeFields: allCtFields,
    perFileFields,
    selectedContentTypeName: selectedCtName
  });

  const { submitForm } = useDocumentSubmission({
    indexId,
    locale: selectedLocale,
    isNewDocument,
    contentTypeFields: allCtFields,
    allowsMultiFileUpload: contentType?.multiFileUpload === true,
    existingDocument: editedDocData?.data.metadata,
    existingDocumentETag: documentETag,
    existingDocumentFileName: editedDocData?.data.metadata?.Cps_FileName as string,
    transformMetadataForSubmit
  });

  const {
    getFieldChangeHandler,
    getChoiceFilterHandler,
    getManualSearchHandler,
    getRefreshHandler,
    loadingFields,
    dynamicChoices,
    getFieldChoices,
    handleFileFieldChange,
    isFieldEnabled,
    runLookupForField
  } = useFieldInteractions({
    ctFields,
    contentTypeFields: allCtFields,
    selectedCtName,
    fieldInteractionCallbacks
  });

  const { grouped: groupedFields, ungrouped: ungroupedFields } = useFieldGroups({
    fields: ctFields,
    groups: contentType?.groups
  });

  const handleSubmit = async (dataItem: any) => {
    await submitForm(dataItem, closeDocumentForm, retry);
  };

  const renderFieldInternal = (
    ctField: ContentTypeFieldResponse,
    formRenderProps: FormRenderProps
  ): React.ReactNode => {
    const fieldTranslation = allFields?.find(x => x.name === ctField.name)?.displayName;
    const choices = dynamicChoices[ctField.name] || ctField.choices || [];
    const isLoading = loadingFields.has(ctField.name);
    const isEnabled = isFieldEnabled(ctField);

    const onChangeDefault = async (value: any): Promise<void> => {
      const handler = getFieldChangeHandler(ctField);
      await handler(ctField.name, value, formRenderProps);
    };

    const onFilterChangeDefault = async (value: any): Promise<void> => {
      const handler = getChoiceFilterHandler(ctField);
      await handler(ctField.name, value, formRenderProps);
    };

    const onSearchDefault = async (value: any): Promise<void> => {
      const handler = getManualSearchHandler(ctField);
      await handler(ctField.name, value, formRenderProps);
    };

    const onRefreshDefault = async (value: any): Promise<void> => {
      const handler = getRefreshHandler(ctField);
      await handler(ctField.name, value, formRenderProps);
    };

    return renderField({
      ctField,
      contentTypeFields: allCtFields,
      fieldTranslation,
      choices,
      isLoading,
      isEnabled,
      formRenderProps,
      runLookupForField,
      onChangeDefault,
      onFilterChangeDefault,
      onSearchDefault,
      onRefreshDefault
    });
  };

  const handleContentTypeChange = (contentTypeName: string) => {
    setSelectedCtName(contentTypeName);
  };

  if (isInitLoading) {
    return <CpsLoader />;
  }

  return (
    <Dialog title={dialogTitle} onClose={closeDocumentForm} contentStyle={{ minWidth: 500 }} className="cpsFormDialog">
      <Form
        key={formKey}
        id={FORM_ID}
        onSubmit={handleSubmit}
        onSubmitClick={() => setAttemptedSubmit(true)}
        initialValues={initialFormValues}
        render={(formRenderProps: FormRenderProps) => {
          return (
            <FormElement noValidate>
              <fieldset className={'k-form-fieldset cpsFormWrapper'}>
                <FormContentTypeField
                  ctsWithCreatePermissions={ctsWithCreatePermissions}
                  editedDocument={editedDocument}
                  onContentTypeChange={handleContentTypeChange}
                  formRenderProps={formRenderProps}
                />

                {renderCustomContent?.()}

                {shouldShowFileUploadTop(contentType) && (
                  <Field
                    name={FILE_FIELD}
                    component={FormUpload}
                    optional={false}
                    editedDocument={editedDocument}
                    validator={requiredValidator}
                    multiple={allowsMultiFileUpload}
                    filesRenderedElsewhere={needsToRenderFilesInTable}
                    onFileSelect={(files: FileList) => {
                      handleFileFieldChange(FILE_FIELD, files, formRenderProps);
                    }}
                    isProcessing={loadingFields.has(FILE_FIELD)}
                    maxFiles={maxFiles}
                  />
                )}

                <FormFieldsSection
                  ungroupedFields={ungroupedFields}
                  groupedFields={groupedFields}
                  renderField={renderFieldInternal}
                  formRenderProps={formRenderProps}
                />

                {shouldShowFileUploadBottom(contentType) && (
                  <Field
                    name={FILE_FIELD}
                    component={FormUpload}
                    optional={false}
                    editedDocument={editedDocument}
                    validator={requiredValidator}
                    multiple={allowsMultiFileUpload}
                    filesRenderedElsewhere={needsToRenderFilesInTable}
                    maxFiles={maxFiles}
                  />
                )}

                <FormFileMetadataTable
                  show={shouldShowFileMetadataTable(
                    needsToRenderFilesInTable,
                    formRenderProps.valueGetter(FILE_FIELD)?.length ?? 0
                  )}
                  files={(formRenderProps.valueGetter(FILE_FIELD) || []).map((item: any) => {
                    if (item.file) {
                      return item;
                    }
                    return {
                      file: item,
                      perFileMetadata: item.perFileMetadata || {}
                    };
                  })}
                  perFileFields={perFileFields}
                  getFieldChoices={getFieldChoices}
                  onChange={updatedFiles => {
                    formRenderProps.onChange(FILE_FIELD, { value: updatedFiles });
                  }}
                />
              </fieldset>

              <FormRetryCheckbox userEmail={user?.email} retry={retry} onRetryChange={setRetry} />
              <FormErrorList errors={formRenderProps.errors} show={attemptedSubmit} />
            </FormElement>
          );
        }}
      />
      <DialogActionsBar>
        <Button type="button" onClick={closeDocumentForm} size={'small'}>
          {translate('custom.common.cancel')}
        </Button>
        <Button form={FORM_ID} type="submit" disabled={!contentType} size={'small'} themeColor={'primary'}>
          {translate('custom.common.save')}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export const DocumentFormContent: React.FC<DocumentFormContentProps> = ({ indexId }) => {
  const renderFieldDefault = (args: DocumentFormFieldRenderArgs): React.ReactNode => {
    return (
      <FieldMapper
        key={args.ctField.name}
        ctField={args.ctField}
        fieldTranslation={args.fieldTranslation}
        choices={args.choices}
        isLoading={args.isLoading}
        onChange={value => {
          void args.onChangeDefault(value);
        }}
        onFilterChange={value => {
          void args.onFilterChangeDefault(value);
        }}
        onSearch={value => {
          void args.onSearchDefault(value);
        }}
        onRefresh={value => {
          void args.onRefreshDefault(value);
        }}
        isEnabled={args.isEnabled}
      />
    );
  };

  return <DocumentFormContentShell indexId={indexId} renderField={renderFieldDefault} />;
};
